/**
 * <s-sticky-header> — reproduces the live store's sticky header.
 *
 * The header scrolls with the page until the shopper is 250px down. The nav row
 * (`[data-sticky-bar]`) then becomes fixed off-screen (`data-stuck`) and, a beat
 * later, slides down into view (`data-opening`). Scrolling back above 250px puts it
 * back in place. This element keeps the row's original height so the page never
 * jumps when the row leaves the flow.
 *
 * Like live, the header stays put instead of sticking when the window is short
 * (700px+ wide and 550px or less tall). Tall mega menus scroll inside the window.
 */
class SStickyHeader extends HTMLElement {
  /** @type {HTMLElement | null} */
  #bar = null;
  #active = false;
  #frame = 0;
  #timer = 0;
  /** @type {AbortController | undefined} */
  #ac;

  connectedCallback() {
    this.#bar = this.querySelector('[data-sticky-bar]');
    if (!this.#bar) return;

    this.#ac = new AbortController();
    const options = { signal: this.#ac.signal, passive: true };

    this.#measure();
    window.addEventListener('scroll', () => this.#schedule(), options);
    window.addEventListener(
      'resize',
      () => {
        this.#measure();
        this.#schedule();
      },
      options
    );
    this.#schedule();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#timer);
    this.#unstick();
  }

  #measure() {
    if (this.#active || !this.#bar) return;
    this.style.height = `${this.#bar.offsetHeight}px`;
  }

  #canStick() {
    if (!this.#bar) return false;
    // Like live, stay put on very short landscape windows. Tall mega menus scroll inside the window instead of blocking the sticky bar.
    return !window.matchMedia('(min-width: 700px) and (max-height: 550px)').matches;
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const shouldStick = window.scrollY > 250 && this.#canStick();
      if (shouldStick === this.#active) return;
      if (shouldStick) {
        this.#stick();
      } else {
        this.#unstick();
      }
    });
  }

  #stick() {
    if (!this.#bar) return;
    this.#active = true;
    this.#bar.setAttribute('data-stuck', '');
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#bar?.setAttribute('data-opening', ''), 100);
  }

  #unstick() {
    this.#active = false;
    clearTimeout(this.#timer);
    this.#bar?.removeAttribute('data-opening');
    this.#bar?.removeAttribute('data-stuck');
  }
}

if (!customElements.get('s-sticky-header')) {
  customElements.define('s-sticky-header', SStickyHeader);
}
